import { create } from 'zustand'
import { fetchFeatureFlags, fetchOwnerContexts } from '../hooks/useApi'
import { useZustand } from '../hooks/useZustand'
import { useContextsStore } from './useContextsStore'
import { ACCESS_DENIED_REASONS } from './contextAccess'
import { VIEW_TYPES } from '../utils/viewType'
import {
    determineIsPublic,
    getUsernameFromUrl,
    getContextNameFromUrl,
    getViewTypeFromUrl,
    getAddContextsFromUrl,
    resolveInitialStopWords,
    bootstrapContextView,
    bootstrapSynthesisView,
    bootstrapSearchView,
    bootstrapAdminView,
} from './bootstrap'

const BOOT_STATES = {
    IDLE: 'idle',
    LOADING_FLAGS: 'loading_flags',
    LOADING_USER: 'loading_user',
    LOADING_CONTEXTS: 'loading_contexts',
    LOADING_GRAPH: 'loading_graph',
    LOADING_STATEMENTS: 'loading_statements',
    READY: 'ready',
    ERROR: 'error',
}

export const useBootstrapStore = create((set, get) => ({
    // Boot state machine
    bootState: BOOT_STATES.IDLE,
    bootError: null,

    // Core data
    flags: {},
    user: null,
    isPublic: false,
    graphOwnerUsername: null,

    // View state derived from URL during bootstrap.
    // viewType ∈ 'context' | 'synthesis' | 'conceptsSynthesis' | 'search'
    // synthesisType ∈ 'contexts' | 'concepts' | null
    // Single source of truth for "what view is the user on?" — read by
    // StatementFilterPanel, useFilterStore, etc. instead of inferring from
    // currentContext or magic context-name prefixes.
    viewType: 'context',
    synthesisType: null,

    // Computed state
    isReady: false,
    isLoading: false,

    /**
     * Main bootstrap function - orchestrates all data fetching in sequence
     * Call this once from App.jsx
     */
    bootstrap: async () => {
        const state = get()

        // Guard: prevent multiple bootstrap calls
        if (state.bootState !== BOOT_STATES.IDLE) {
            console.log('Bootstrap already running or completed, skipping')
            return
        }

        set({ bootState: BOOT_STATES.LOADING_FLAGS, isLoading: true })

        try {
            // Admin read-only graph viewer (routes/admin.js adminGraphPage):
            // the server-injected window.__ADMIN_VIEW__ short-circuits the
            // normal URL-driven bootstrap entirely — a single graph is
            // seeded via the master-token admin data endpoint instead of the
            // flags/user/contexts/graph sequence below.
            if (typeof window !== 'undefined' && window.__ADMIN_VIEW__?.adminView) {
                const adminPayload = window.__ADMIN_VIEW__
                await bootstrapAdminView({
                    userId: adminPayload.graphOwner.id,
                    contextName: adminPayload.contextName,
                    graphOwner: adminPayload.graphOwner,
                    addContexts: adminPayload.addContexts || [],
                })
                set({
                    // Admin view has no flags endpoint call (see Step 1 below) —
                    // force new_3d_graph on so LazyGraph's
                    // `!flags.new_3d_graph` branch doesn't render an
                    // ErrorScreen instead of the seeded graph.
                    flags: { new_3d_graph: true },
                    graphOwnerUsername: adminPayload.graphOwner.username,
                    bootState: BOOT_STATES.READY,
                    isReady: true,
                    isLoading: false,
                })
                return
            }

            // No dependency on flags — fetch the user in parallel, awaited in
            // step 2. The no-op catch only silences the interim unhandled-
            // rejection report; step 2's await still receives the error.
            const userResponsePromise = fetch('/react/user').then(response => {
                // A non-2xx must reject — resolving it would stamp
                // isAuthenticated on an error payload.
                if (!response.ok)
                    throw new Error(`/react/user responded ${response.status}`)
                return response.json()
            })
            userResponsePromise.catch(() => {})

            // ═══════════════════════════════════════════════════════════
            // Step 1: Load feature flags
            // ═══════════════════════════════════════════════════════════
            console.log('[InfraNodus] Step 1: Loading user flags')
            // Default new_3d_graph to true for anonymous users (flags endpoint requires auth).
            // TODO: remove this default once new_3d_graph flag is obsolete.
            let flags = { new_3d_graph: true }
            try {
                flags = await fetchFeatureFlags()
            } catch (err) {
                console.warn(
                    '[Bootstrap] Failed to load flags, using defaults:',
                    err
                )
            }
            set({ flags, bootState: BOOT_STATES.LOADING_USER })

            // ═══════════════════════════════════════════════════════════
            // Step 2: Load user info
            // ═══════════════════════════════════════════════════════════
            console.log('[InfraNodus] Step 2: Loading user information')
            const urlUsername = getUsernameFromUrl()

            let user = null
            let isPublic = true

            try {
                const userResponse = await userResponsePromise
                // /react/user is JWT-gated, so a successful response proves the
                // viewer is authenticated and its `username` is the viewer's own.
                if (!userResponse || !userResponse.username)
                    throw new Error('/react/user payload has no username')
                user = { ...userResponse, isAuthenticated: true }
                isPublic = determineIsPublic(userResponse.username, urlUsername)
            } catch (err) {
                console.warn(
                    '[Bootstrap] Failed to load user, treating as public:',
                    err
                )
                user = { username: urlUsername, isAuthenticated: false }
                isPublic = true
            }

            // Determine effective username (logged-in user or URL user for public)
            const graphOwnerUsername = isPublic ? urlUsername : user.username

            // Public views are a new-app-only experience: a shared graph link
            // always renders through the React 3D graph, with no legacy
            // fallback. A logged-in viewer who happens not to have the
            // `new_3d_graph` experimental flag would otherwise hit
            // LazyGraph's `!flags.new_3d_graph` → ErrorScreen branch and see a
            // blank graph (anonymous viewers dodge this via the `true` default
            // since the flags endpoint requires auth). Force it on for public
            // views so the viewer's personal flag can't disable someone else's
            // shared graph.
            if (isPublic) {
                flags = { ...flags, new_3d_graph: true }
            }

            set({
                flags,
                user: { ...user, isPublic },
                isPublic,
                graphOwnerUsername,
                bootState: BOOT_STATES.LOADING_CONTEXTS,
            })

            // ═══════════════════════════════════════════════════════════
            // Step 3: Load contexts (single endpoint, viewer-aware)
            // ═══════════════════════════════════════════════════════════
            console.log('[InfraNodus] Step 3: Loading contexts')
            // No-flicker: keep the existing arrays visible while the new
            // fetch is in-flight. setContexts/markAccessDenied below flip
            // loadedContextList back to true.
            useContextsStore.getState().startBootstrap()
            // Pre-populate currentGraphOwner with the URL owner so the
            // drawer header / testid anchor read the correct username
            // immediately instead of the previous owner for ~100-500ms
            // until the fetch resolves. id/accessMode stay null until
            // the response REPLACES this via setCurrentGraphOwner below.
            if (graphOwnerUsername) {
                useContextsStore.getState().setCurrentGraphOwner({
                    username: graphOwnerUsername,
                    id: null,
                    accessMode: null,
                })
            }

            try {
                const response = await fetchOwnerContexts(graphOwnerUsername)

                useContextsStore
                    .getState()
                    .setCurrentGraphOwner(response.graphOwner ?? null)
                if (response.accessDenied) {
                    useContextsStore
                        .getState()
                        .markAccessDenied(response.accessDenied)
                } else {
                    useContextsStore
                        .getState()
                        .setContexts(response.contextRecords || [])
                    useContextsStore
                        .getState()
                        .setOpenLimit(response.openLimit ?? null)
                }
            } catch (err) {
                console.warn('[Bootstrap] Failed to load contexts:', err)
                useContextsStore.getState().markAccessDenied({
                    reason: ACCESS_DENIED_REASONS.UNAVAILABLE,
                })
            }

            set({ bootState: BOOT_STATES.LOADING_GRAPH })

            // ═══════════════════════════════════════════════════════════
            // Step 4: Load graph data (context, synthesis, or search)
            // ═══════════════════════════════════════════════════════════
            const contextName = getContextNameFromUrl()
            const addContexts = getAddContextsFromUrl()
            // NOTE: pass the ACTUALLY LOGGED-IN username here, not
            // `graphOwnerUsername`. In public mode `graphOwnerUsername`
            // equals the URL owner, which would misclassify anonymous
            // visits to /:someone-else (no second segment) as that
            // user's top-synthesis view. The /:self → top-synthesis
            // shorthand should only fire for the owner themselves.
            const viewType = getViewTypeFromUrl(user?.username || null)
            const synthesisType =
                viewType === VIEW_TYPES.SYNTHESIS
                    ? 'contexts'
                    : viewType === VIEW_TYPES.CONCEPTS_SYNTHESIS
                    ? 'concepts'
                    : null

            // Publish view state so consumers (filter store, filter panel)
            // don't have to re-derive it from the URL or sniff context names.
            set({ viewType, synthesisType })

            const stopWords = resolveInitialStopWords()

            // Per-view bootstrappers handle data load + statement processing.
            // Each one preserves its own try/catch boundary so a failure in
            // one view type doesn't prevent the boot lifecycle from completing.
            const onLoadingStatements = () =>
                set({ bootState: BOOT_STATES.LOADING_STATEMENTS })

            if (synthesisType) {
                await bootstrapSynthesisView({
                    synthesisType,
                    stopWords,
                    graphOwnerUsername,
                    isPublic,
                    onLoadingStatements,
                })
            } else if (viewType === VIEW_TYPES.SEARCH) {
                bootstrapSearchView()
            } else {
                await bootstrapContextView({
                    contextName,
                    addContexts,
                    stopWords,
                    graphOwnerUsername,
                    isPublic,
                    onLoadingStatements,
                })
            }

            // ═══════════════════════════════════════════════════════════
            // All done!
            // ═══════════════════════════════════════════════════════════
            console.log('[Bootstrap] Complete!')
            set({
                bootState: BOOT_STATES.READY,
                isReady: true,
                isLoading: false,
            })
        } catch (error) {
            console.error('[Bootstrap] Fatal error:', error)
            set({
                bootState: BOOT_STATES.ERROR,
                bootError: error,
                isLoading: false,
            })
        }
    },

    /**
     * Reset bootstrap state (useful for testing or re-initialization)
     */
    reset: () => {
        set({
            bootState: BOOT_STATES.IDLE,
            bootError: null,
            flags: {},
            user: null,
            isPublic: false,
            graphOwnerUsername: null,
            viewType: 'context',
            synthesisType: null,
            isReady: false,
            isLoading: false,
        })
    },

    /**
     * Get experimentalEnabled derived state
     */
    getExperimentalEnabled: () => {
        const flags = get().flags
        return (
            Object.keys(flags).length > 0 &&
            Object.values(flags).every(value => value === true)
        )
    },
}))

// Export boot states for external use
export { BOOT_STATES }

// Username of the AUTHENTICATED viewer, or null. Never read `user.username`
// directly for identity: on anonymous public views it holds the graph OWNER's
// name from the URL fallback — identifying with it would tag anonymous
// visitors as the owner.
export const getAuthenticatedUsername = () => {
    const { user } = useBootstrapStore.getState()
    return user && user.isAuthenticated ? user.username : null
}
